import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import Breadcrumb from 'components/Breadcrumb';
import {
  AuditedAccount,
  AuditedUser,
  InactivityRunSummary,
  accountsService,
} from './services/AccountsService';
import { logger } from 'utils/logger';

const log = logger.getLogger('InactivityCenterPage');

type DialogPhase = 'closed' | 'confirm' | 'running' | 'error' | 'success';

const AUTO_CLOSE_SECONDS = 15;

const formatActivityDate = (val: number | string | null | undefined): string => {
  if (val === null || val === undefined) return '—';
  const d = new Date(val as any);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const InactivityCenterPage: React.FC = () => {
  const theme = useTheme();

  // ── Preview data ────────────────────────────────────────────────────────────
  const [summary, setSummary] = useState<InactivityRunSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  // false = review accounts pending inactivity deactivation; true = review persistent accounts that
  // would be inactive but are exempt (audit-only preview).
  const [persistentMode, setPersistentMode] = useState<boolean>(false);

  // ── Table state ─────────────────────────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  // ── Deactivation dialog state ────────────────────────────────────────────────
  const [dialogPhase, setDialogPhase] = useState<DialogPhase>('closed');
  const [runErrorDetail, setRunErrorDetail] = useState<string>('');
  const [runResult, setRunResult] = useState<InactivityRunSummary | null>(null);
  const [countdown, setCountdown] = useState<number>(AUTO_CLOSE_SECONDS);

  // ── Fetch preview ────────────────────────────────────────────────────────────
  const fetchPreview = useCallback(async () => {
    try {
      setLoading(true);
      log.log(`Fetching inactivity deactivation preview… (onlyPersistent=${persistentMode})`);
      const result = await accountsService.previewDeactivation(persistentMode);
      setSummary(result);
      setError(null);
    } catch (err) {
      log.error('Error fetching deactivation preview:', err);
      setError('Failed to load deactivation preview. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [persistentMode]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  // ── Auto-close countdown on success ─────────────────────────────────────────
  useEffect(() => {
    if (dialogPhase !== 'success') return;
    setCountdown(AUTO_CLOSE_SECONDS);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setDialogPhase('closed');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [dialogPhase]);

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleRunDeactivation = async () => {
    setDialogPhase('running');
    try {
      log.log('Running mass deactivation…');
      const result = await accountsService.runDeactivation();
      log.log(`Deactivation complete: ${result.accountsDeactivated} accounts, ${result.usersDisabled} users`);
      setRunResult(result);
      setDialogPhase('success');
      fetchPreview(); // refresh preview data in the background
    } catch (err: any) {
      log.error('Error running deactivation:', err);
      let detail = '';
      if (err?.response?.data) {
        detail =
          typeof err.response.data === 'string'
            ? err.response.data
            : JSON.stringify(err.response.data, null, 2);
      } else if (err?.message) {
        detail = err.message;
      } else {
        detail = String(err);
      }
      setRunErrorDetail(detail);
      setDialogPhase('error');
    }
  };

  const handleCloseDialog = () => {
    if (dialogPhase === 'running') return;
    setDialogPhase('closed');
  };

  // Switch between the inactive-accounts review and the persistent-accounts (audit-only) review.
  // fetchPreview re-runs automatically because it depends on persistentMode.
  const handleToggleMode = () => {
    setPersistentMode((prev) => !prev);
    setPage(0);
    setExpandedId(null);
  };

  // Export the currently-shown accounts (inactive or persistent view) to a CSV, one row per user.
  const handleExportCsv = () => {
    const accountsToExport = summary?.accounts ?? [];
    const headers = ['account id', 'account name', 'user id', 'username'];

    const escapeCell = (value: string): string =>
      /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

    const rows: string[][] = [headers];
    accountsToExport.forEach((account) => {
      if (account.users.length === 0) {
        // Keep the account in the export even when it has no users to list.
        rows.push([String(account.id), account.name ?? '', '', '']);
        return;
      }
      account.users.forEach((user) => {
        rows.push([String(account.id), account.name ?? '', String(user.id), user.username ?? '']);
      });
    });

    const csv = rows.map((row) => row.map(escapeCell).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `inactivity-${persistentMode ? 'persistent' : 'inactive'}-export-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // ── Table helpers ─────────────────────────────────────────────────────────────
  const accounts: AuditedAccount[] = summary?.accounts ?? [];
  const totalItems = accounts.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const pagedAccounts = accounts.slice(page * rowsPerPage, (page + 1) * rowsPerPage);

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleRowClick = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const getPaginationInfo = () => {
    if (totalItems === 0) return '0 items';
    const start = page * rowsPerPage + 1;
    const end = Math.min((page + 1) * rowsPerPage, totalItems);
    return `${start} – ${end} of ${totalItems} items`;
  };

  const hasResults =
    !loading && !error && summary != null && summary.accountsDeactivated > 0;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
      <Breadcrumb
        items={[
          {
            label: 'Accounts',
            path: '/accounts',
            icon: <BusinessIcon fontSize="small" sx={{ fontSize: '1rem' }} />,
          },
          { label: 'Inactivity Center' },
        ]}
      />

      {/* Page header row */}
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} mb={3}>
        <Box>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}
          >
            Inactivity Center
          </Typography>

          {loading && (
            <Typography variant="body2" color="text.secondary">
              Loading inactivity data…
            </Typography>
          )}
          {!loading && error && (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          )}
          {!loading && !error && persistentMode && (<>
            <Typography variant="h6" sx={{ fontWeight: 400 }}>
              {(summary?.accountsDeactivated ?? 0)} persistent account
              {(summary?.accountsDeactivated ?? 0) !== 1 ? 's' : ''} and{' '}
              {(summary?.usersDisabled ?? 0)} user
              {(summary?.usersDisabled ?? 0) !== 1 ? 's' : ''} have been inactive for four months.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Persistent accounts are exempt from automatic deactivation.
            </Typography>
          </>)}
          {!loading && !error && !persistentMode && (
            <Typography variant="h6" sx={{ fontWeight: 400 }}>
              {(summary?.accountsDeactivated ?? 0)} account
              {(summary?.accountsDeactivated ?? 0) !== 1 ? 's' : ''} and{' '}
              {(summary?.usersDisabled ?? 0)} user
              {(summary?.usersDisabled ?? 0) !== 1 ? 's' : ''} have been inactive for four months.
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1.5} sx={{ flexShrink: 0, mt: 0.5 }}>
          <Button
            variant="outlined"
            onClick={handleToggleMode}
            disabled={loading}
            sx={{
              px: 2.5,
              py: 1,
              borderRadius: '12px',
              fontWeight: 600,
              textTransform: 'none',
            }}
          >
            {persistentMode ? 'View Inactive' : 'View Persistent'}
          </Button>

          {hasResults && (
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportCsv}
              sx={{
                px: 2.5,
                py: 1,
                borderRadius: '12px',
                fontWeight: 600,
                textTransform: 'none',
              }}
            >
              CSV Export
            </Button>
          )}

          {hasResults && (
            <Tooltip
              title={persistentMode ? 'Persistent accounts are exempt from automatic deactivation' : ''}
            >
              <span>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<DeleteSweepIcon />}
                  onClick={() => setDialogPhase('confirm')}
                  disabled={persistentMode}
                  sx={{
                    px: 2.5,
                    py: 1,
                    borderRadius: '12px',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 14px rgba(0,0,0,0.2)',
                    },
                  }}
                >
                  Run Deactivation
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* ── Preview table ───────────────────────────────────────────────────────── */}
      <Fade in={true} timeout={600}>
        <Paper
          elevation={2}
          sx={{
            width: '100%',
            mb: 3,
            borderRadius: 2,
            overflow: 'hidden',
            '&:hover': { boxShadow: '0 8px 16px 0 rgba(0,0,0,0.1)' },
          }}
        >
          <TableContainer>
            <Table sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow>
                  {['Account Name', 'Users', 'Last Activity', ''].map((col, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                        letterSpacing: '0.05em',
                        backgroundColor: theme.palette.background.default,
                        borderBottom: `2px solid ${theme.palette.divider}`,
                        width: i === 3 ? 56 : undefined,
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Loading preview…
                    </TableCell>
                  </TableRow>
                )}
                {!loading && error && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ color: 'error.main' }}>
                      {error}
                    </TableCell>
                  </TableRow>
                )}
                {!loading && !error && pagedAccounts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No accounts would be deactivated.
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  !error &&
                  pagedAccounts.map((account) => {
                    const isExpanded = expandedId === account.id;
                    return (
                      <React.Fragment key={account.id}>
                        <TableRow
                          hover
                          onClick={() => handleRowClick(account.id)}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: isExpanded
                              ? alpha(theme.palette.primary.main, 0.06)
                              : undefined,
                            '&:nth-of-type(odd)': {
                              backgroundColor: isExpanded
                                ? alpha(theme.palette.primary.main, 0.06)
                                : alpha(theme.palette.background.default, 0.4),
                            },
                          }}
                        >
                          <TableCell sx={{ fontWeight: 500 }}>{account.name}</TableCell>
                          <TableCell>{account.users.length}</TableCell>
                          <TableCell>{formatActivityDate(account.effectiveLastActivity)}</TableCell>
                          <TableCell align="right" sx={{ pr: 1 }}>
                            <Tooltip title={isExpanded ? 'Collapse' : 'Expand users'}>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRowClick(account.id);
                                }}
                              >
                                {isExpanded ? (
                                  <ExpandLessIcon fontSize="small" />
                                ) : (
                                  <ExpandMoreIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={4} sx={{ p: 0, border: 0 }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box
                                sx={{
                                  mx: 3,
                                  my: 1.5,
                                  borderRadius: 1,
                                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                  overflow: 'hidden',
                                }}
                              >
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      {['Username', 'Type', 'Currently Enabled'].map((col) => (
                                        <TableCell
                                          key={col}
                                          sx={{
                                            fontWeight: 600,
                                            fontSize: '0.7rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                            backgroundColor: alpha(
                                              theme.palette.background.default,
                                              0.8,
                                            ),
                                            borderBottom: `1px solid ${theme.palette.divider}`,
                                          }}
                                        >
                                          {col}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {account.users.map((user: AuditedUser) => (
                                      <TableRow key={user.id} hover>
                                        <TableCell sx={{ fontSize: '0.85rem' }}>
                                          {user.username}
                                        </TableCell>
                                        <TableCell>
                                          <Tooltip title={user.robotic ? 'Robotic' : 'Human'}>
                                            <Box
                                              component="span"
                                              sx={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                fontSize: '0.8rem',
                                                color: user.robotic
                                                  ? theme.palette.warning.main
                                                  : theme.palette.text.secondary,
                                              }}
                                            >
                                              {user.robotic ? (
                                                <SmartToyIcon fontSize="inherit" />
                                              ) : (
                                                <PersonIcon fontSize="inherit" />
                                              )}
                                              {user.robotic ? 'Robotic' : 'Human'}
                                            </Box>
                                          </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: user.enabled
                                                ? theme.palette.success.main
                                                : theme.palette.text.disabled,
                                              fontWeight: 600,
                                            }}
                                          >
                                            {user.enabled ? 'Yes' : 'No'}
                                          </Typography>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                    {account.users.length === 0 && (
                                      <TableRow>
                                        <TableCell colSpan={3} align="center">
                                          No users
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination footer */}
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderTop: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Box display="flex" alignItems="center">
              <Typography
                variant="body2"
                sx={{ mr: 2, color: alpha(theme.palette.text.primary, 0.7), fontWeight: 500 }}
              >
                Items per page:
              </Typography>
              <FormControl
                size="small"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    transition: 'all 0.2s',
                    '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                  },
                }}
              >
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  sx={{ height: 36, minWidth: 70, fontWeight: 500 }}
                >
                  {[5, 10, 25, 100].map((v) => (
                    <MenuItem key={v} value={v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" alignItems="center">
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, color: alpha(theme.palette.text.primary, 0.7) }}
              >
                {getPaginationInfo()}
              </Typography>
              <Box ml={1.5} display="flex" alignItems="center">
                <Tooltip title="Previous page">
                  <span>
                    <IconButton
                      size="small"
                      disabled={page === 0}
                      onClick={() => setPage((p) => p - 1)}
                      sx={{
                        mx: 0.5,
                        transition: 'all 0.2s',
                        color: page === 0 ? undefined : theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <NavigateBeforeIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Next page">
                  <span>
                    <IconButton
                      size="small"
                      disabled={page >= totalPages - 1}
                      onClick={() => setPage((p) => p + 1)}
                      sx={{
                        mx: 0.5,
                        transition: 'all 0.2s',
                        color: page >= totalPages - 1 ? undefined : theme.palette.primary.main,
                        '&:hover': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.08),
                          transform: 'scale(1.05)',
                        },
                      }}
                    >
                      <NavigateNextIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Fade>

      {/* ── Deactivation dialog ─────────────────────────────────────────────────── */}
      <Dialog
        open={dialogPhase !== 'closed'}
        onClose={handleCloseDialog}
        disableEscapeKeyDown={dialogPhase === 'running'}
        maxWidth={dialogPhase === 'error' ? 'md' : 'sm'}
        fullWidth
      >
        {/* Confirm ──────────────────────────────────────────────────────────────── */}
        {dialogPhase === 'confirm' && (
          <>
            <DialogTitle sx={{ fontWeight: 600 }}>Run Deactivation?</DialogTitle>
            <DialogContent>
              <Typography gutterBottom>
                This will immediately deactivate{' '}
                <strong>
                  {summary?.accountsDeactivated ?? 0} account
                  {(summary?.accountsDeactivated ?? 0) !== 1 ? 's' : ''}
                </strong>{' '}
                and disable{' '}
                <strong>
                  {summary?.usersDisabled ?? 0} user
                  {(summary?.usersDisabled ?? 0) !== 1 ? 's' : ''}
                </strong>
                .
              </Typography>
              <Typography gutterBottom>
                This operation can take some time to complete. Do not close this window once it has
                started.
              </Typography>
              <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                This action cannot be undone through this interface.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseDialog}>Cancel</Button>
              <Button
                onClick={handleRunDeactivation}
                variant="contained"
                color="error"
                startIcon={<DeleteSweepIcon />}
              >
                Deactivate
              </Button>
            </DialogActions>
          </>
        )}

        {/* Running ──────────────────────────────────────────────────────────────── */}
        {dialogPhase === 'running' && (
          <>
            <DialogTitle sx={{ fontWeight: 600 }}>Deactivating Accounts…</DialogTitle>
            <DialogContent>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={3}
                py={3}
              >
                <CircularProgress size={56} />
                <Typography align="center" color="text.secondary">
                  Processing deactivations — this may take some time.
                  <br />
                  Please do not close this window.
                </Typography>
              </Box>
            </DialogContent>
            {/* No actions intentionally: dialog is non-dismissable while running */}
          </>
        )}

        {/* Error ─────────────────────────────────────────────────────────────────── */}
        {dialogPhase === 'error' && (
          <>
            <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>
              Deactivation Failed
            </DialogTitle>
            <DialogContent>
              <Typography gutterBottom>
                An unexpected error occurred. Please send the details below to Engineering.
              </Typography>
              <Box
                component="pre"
                sx={{
                  mt: 1.5,
                  p: 2,
                  borderRadius: 1,
                  backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#1e1e1e',
                  color: '#f8f8f2',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  overflow: 'auto',
                  maxHeight: 320,
                }}
              >
                {runErrorDetail || '(no additional detail)'}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseDialog} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}

        {/* Success ──────────────────────────────────────────────────────────────── */}
        {dialogPhase === 'success' && (
          <>
            <DialogTitle sx={{ fontWeight: 600, color: 'success.main' }}>
              Deactivation Complete
            </DialogTitle>
            <DialogContent>
              <Typography gutterBottom>
                Successfully deactivated{' '}
                <strong>
                  {runResult?.accountsDeactivated ?? 0} account
                  {(runResult?.accountsDeactivated ?? 0) !== 1 ? 's' : ''}
                </strong>{' '}
                and disabled{' '}
                <strong>
                  {runResult?.usersDisabled ?? 0} user
                  {(runResult?.usersDisabled ?? 0) !== 1 ? 's' : ''}
                </strong>
                .
              </Typography>
              <Typography gutterBottom>
                Inactive accounts can be found using the{' '}
                <strong>Account Status: Inactive</strong> filter on the Accounts list.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This dialog will close in {countdown} second{countdown !== 1 ? 's' : ''}.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseDialog} variant="contained" color="success">
                Close Now
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default InactivityCenterPage;
